/**
 * 日本語「近くで探す（near me）」コピー — 第2波：主要9クラスター以外の全サービス。
 *
 * 手書き（生成時・実行時ともAIクレジット消費ゼロ）。事実関係は
 * ./registry.ts / ./en-copy-2.ts / ./zh-copy-2.ts と整合させています。
 *  - タイはハーグ・アポスティーユ条約の締約国ではありません。国外で使用する
 *    タイの公文書は外務省領事局の認証を受け、必要に応じて在タイ当該国大使館の
 *    認証を追加します。
 *  - 料金は掲載せず案件ごとの見積り。処理期間・受理の保証はしません。
 *  - 当方は「代行するだけ」ではなく、順序と証拠を先に整理する顧問型の関与です。
 */

import type { NearServiceKey } from "./registry";
import type { NearJaCopy } from "./ja-copy";

export const NEAR_JA2_KEYS = [
  "marriage",
  "longstay",
  "corporate",
  "immigration",
  "estate",
  "civilreg",
  "family",
  "accounting",
  "interpreter",
  "embassy",
  "ip",
  "litigation",
  "pdpa",
  "misc-official",
  "council",
  "due-diligence",
  "pension",
  "work-permit",
  "expert",
] as const satisfies readonly NearServiceKey[];

export type NearJa2Key = (typeof NEAR_JA2_KEYS)[number];

export const NEAR_JA2_COPY: Record<NearJa2Key, NearJaCopy> = {
  marriage: {
    label: "タイでの国際結婚に関する書類サービス",
    definition:
      "タイでの国際結婚は一度の予約で完了する手続きではなく、書類の連鎖です。まず在タイ自国大使館で婚姻要件具備証明（独身証明）を取得し、タイ語の認証翻訳を作成、外務省領事局の認証を受けたうえで、区役所（アンプー）で婚姻登録を行います。",
    chain: [
      "所属する大使館の現在の婚姻要件具備証明・独身証明の手順と予約規則を確認します。",
      "大使館の証明書を取得後、認証翻訳者がタイ語に翻訳します。",
      "翻訳を外務省領事局（Legalization Division）で認証します。",
      "当事者双方が区役所に出向き婚姻登録を行います。",
      "国外で使用するため、婚姻証明書の謄本・翻訳・認証を手配します。",
    ],
    checklist: [
      "外国人配偶者のパスポートと入国スタンプのページ。",
      "タイ人配偶者のIDカードと住居登録簿（タビアンバーン）。",
      "大使館発行の婚姻要件具備証明または独身証明。",
      "婚姻歴がある場合は離婚証明または配偶者の死亡証明。",
      "区役所での登録に必要な証人2名。",
    ],
    pitfalls: [
      "外務省認証が終わる前に区役所の予約を入れてしまう。",
      "大使館証明書の氏名表記がパスポートと一致していない。",
      "前婚の終了を示す書類が不足している。",
      "タイの婚姻証明書がそのまま国外で使えると誤解する。",
    ],
    remote:
      "翻訳・認証・工程管理は遠隔で対応できます。大使館の予約と婚姻登録自体は当事者の出席が必要です。",
  },
  longstay: {
    label: "長期滞在・リタイアメント延長の書類サービス",
    definition:
      "長期滞在の延長は、提出された証拠に基づき入国管理局が判断します。当方は書類準備と助言を担当し、証拠を区分ごとに整え、受理されやすい形式に整理します。",
    chain: [
      "正しい延長カテゴリーと現行の証拠リストを確認します。",
      "お手持ちの書類と比較して不足点を洗い出します。",
      "財産・住所・保険の証明を受理される形式で準備します。",
      "提出順に整理し、チェックリストを添えます。",
      "提出時に同行・支援し、追加資料の求めに対応します。",
    ],
    checklist: [
      "パスポート、現行ビザ、TM.6／出入国記録。",
      "TM.30 住所申告と賃貸契約または所有権証明。",
      "形式・預入期間の要件を満たす銀行書類。",
      "カテゴリーが求める健康保険の書類。",
      "家族カテゴリーの場合は婚姻・親族関係書類。",
    ],
    pitfalls: [
      "銀行証明が必要な預入期間を満たしていない。",
      "TM.30 の申告漏れ — 延長審査で最初に確認されます。",
      "滞在可能期限の直前になってから提出する。",
      "延長が保証されると考える — 判断は審査官が行います。",
    ],
    remote: "書類の準備と点検は遠隔で完了できます。入管の予約はご本人の出席が必要です。",
  },
  corporate: {
    label: "会社書類・法人認証サービス",
    definition:
      "会社書類の業務は、会社証明書、DBD 登記簿抄本、取締役会決議、授権状、およびそれらの国外使用のための認証を含みます。口座開設、入札、代理人授権、海外子会社の設立などで使用されます。",
    chain: [
      "提出先が求める正確な書類リストと発行後の有効期間を確認します。",
      "最新の DBD 抄本と会社証明書を取得します。",
      "相手方が受け入れる文言で決議書や授権状を準備します。",
      "公証業務弁護士が署名と写しを認証します。",
      "国外使用の場合は外務省認証と在タイ当該国大使館の認証を行います。",
    ],
    checklist: [
      "会社証明書と DBD 登記簿抄本（直近発行のもの）。",
      "株主名簿と事業目的の抄本。",
      "授権を受けた取締役のパスポートまたはタイIDカード。",
      "決議書・契約書の最終ドラフト。",
      "提出先の書面による要件。",
    ],
    pitfalls: [
      "抄本の発行日が相手方の有効期間を超えている。",
      "認証の予約前に署名してしまう。",
      "会社証明書上の権限がない取締役が決議に署名する。",
      "タイ語原本の印章・エンボス文字が翻訳で抜け落ちる。",
    ],
    remote: "署名の立会い以外は、すべて遠隔で対応できます。",
  },
  immigration: {
    label: "入国管理書類の準備・顧問サービス",
    definition:
      "入管関連の申請 — 延長、再入国許可、ステータス変更、各種届出 — はそれぞれ証拠リストが異なります。当方は準備と順序付けを担当し、最終的な判断はタイ入国管理局が行います。",
    chain: [
      "申請種別、管轄オフィス、証拠リストを確認します。",
      "現在の滞在期限と過去の申請との整合性を点検します。",
      "補足書類を準備し、必要な認証を行います。",
      "提出順に整理し、窓口用のチェックリストを添えます。",
      "追跡し、追加資料の求めに対応します。",
    ],
    checklist: [
      "パスポートと全てのビザ・スタンプのページ。",
      "TM.30 住所申告と居所の証明。",
      "カテゴリーに応じた就労・就学・家族関係の書類。",
      "現行規格に合う写真。",
      "代理提出の場合は委任状。",
    ],
    pitfalls: [
      "管轄外のオフィスに提出してしまう。",
      "TM.30・賃貸契約・申請書の住所が一致していない。",
      "証拠を集めている間に滞在期限を超過する。",
      "オフィスの現行リストではなく、古いネット情報に従う。",
    ],
    remote: "準備と助言は遠隔で可能です。窓口の予約はご本人の出席が必要です。",
  },
  estate: {
    label: "遺言・遺産・相続の書類サービス",
    definition:
      "タイの相続は裁判所手続と登記手続の二本立てです。遺言（または法定相続）に始まり、裁判所が遺産管理人を選任し、資産ごとの登記機関で名義変更を行います。国際相続では翻訳と認証も必要です。",
    chain: [
      "遺産の全体像を整理します：資産、所在地、相続人、遺言の有無。",
      "資産ごとに裁判所選任の管理人が必要かを確認します。",
      "身分関係・相続関係の証明を整え、外国書類には認証翻訳を付します。",
      "弁護士が管理人選任を申立て、期日に出廷します。",
      "裁判所命令に基づき、各登記機関で資産の名義変更を行います。",
    ],
    checklist: [
      "死亡証明書。外国発行の場合は認証翻訳付き。",
      "遺言書（ある場合）と原本の保管場所。",
      "相続関係の証明：住居登録簿、出生・婚姻証明。",
      "資産の証明：土地権利証、銀行取引明細、株主名簿。",
      "相続人全員のパスポートまたはIDカード。",
    ],
    pitfalls: [
      "管理人選任命令の前に銀行や土地の名義変更を試みる。",
      "外国書類に認証されたタイ語翻訳が付いていない。",
      "相続人の漏れにより分配が無効となるおそれ。",
      "外国の遺言だけでタイの土地を処分できると誤解する。",
    ],
    remote:
      "書類整理と起案は遠隔で対応できます。期日は弁護士が出廷し、裁判所が求める場合のみご本人の出席が必要です。",
  },
  civilreg: {
    label: "住民登録・身分登録の書類サービス",
    definition:
      "身分登録は区役所が保管する出生・婚姻・離婚・死亡・改名・住居登録の記録を扱います。訂正や謄本の取得はそれぞれ証拠リストが異なり、国外使用には翻訳と認証が必要です。",
    chain: [
      "記録の種類と、その記録を保管する区役所を特定します。",
      "認証謄本を申請するか、訂正の申立てを行います。",
      "そのオフィスが指定する補足証拠を揃えます。",
      "海外使用のための認証翻訳を作成します。",
      "求められる場合は外務省認証と大使館認証を行います。",
    ],
    checklist: [
      "タイIDカードと住居登録簿。外国人はパスポート。",
      "既にお持ちの証明書や謄本。",
      "訂正内容を裏づける証拠（学校・病院・裁判所の書類）。",
      "当方が代理申請する場合の委任状。",
    ],
    pitfalls: [
      "原記録を保管していないオフィスに申請する。",
      "訂正申立てに正しいデータを示す書類がない。",
      "先に翻訳したが謄本を再取得することになり作業が無駄になる。",
      "同じ家族の書類で氏名のローマ字表記が揃っていない。",
    ],
    remote: "申請と追跡は当方で代行できます。一部の訂正は記録本人の出席が必要です。",
  },
  family: {
    label: "家族法に関する書類サービス",
    definition:
      "家族に関する案件 — 離婚、親権、認知、後見人選任、婚前契約 — は、内容により区役所（合意方式）または裁判所で扱われます。最初に経路を正しく選ぶことで、遅延の大半を避けられます。",
    chain: [
      "区役所での合意方式で足りるか、裁判所が必要かを評価します。",
      "執行可能な文言で合意書または訴状を起案します。",
      "身分・婚姻状況の証明を整え、外国書類には認証翻訳を付します。",
      "区役所で登録するか、裁判所に提出します。",
      "国外使用のための認証謄本・翻訳・認証を取得します。",
    ],
    checklist: [
      "婚姻証明書、必要に応じて婚前契約の書類。",
      "子の出生証明書。",
      "双方のタイIDカード、住居登録簿、パスポート。",
      "親権・扶養の取り決めに関する証拠。",
      "外国裁判所の命令とその認証翻訳。",
    ],
    pitfalls: [
      "合意離婚の登録時に財産と親権を書面で定めていない。",
      "承認手続を経ずに外国の命令をタイで使おうとする。",
      "文言が原因で区役所や銀行が後から履行を拒む。",
      "タイと外国の記録が自動的に同期すると誤解する（同期しません）。",
    ],
    remote: "起案と証拠の点検は遠隔で可能です。登録と期日は出席が必要です。",
  },
  accounting: {
    label: "会計・税務・監査書類サービス",
    definition:
      "会計書類の業務は、帳簿記帳、財務諸表、監査ファイル、税務申告、そして銀行・入札委員会・海外親会社が求める場合の認証と翻訳を含みます。",
    chain: [
      "提出先が必要とする財務諸表と対象期間を確認します。",
      "帳簿を照合し、財務諸表一式を作成します。",
      "監査済財務諸表が必要な場合は監査人が署名します。",
      "財務諸表と監査注記の認証翻訳を作成します。",
      "公証業務弁護士が認証し、国外使用の場合は外務省認証を行います。",
    ],
    checklist: [
      "会社証明書と税務登録の書類。",
      "対象期間の試算表、総勘定元帳、銀行取引明細。",
      "前年度の提出済財務諸表と納税申告書。",
      "提出先の書式要件。",
    ],
    pitfalls: [
      "監査済財務諸表を求められているのに管理会計資料を提出する。",
      "監査人の注記の原文言が翻訳で失われる。",
      "後に修正される中間版を先に認証してしまう。",
      "書類を集めている間に申告期限を過ぎる。",
    ],
    remote: "全工程を遠隔で対応でき、紙の認証原本は宅配便でお届けします。",
  },
  interpreter: {
    label: "法廷・各種予約の通訳サービス",
    definition:
      "通訳はその場で行う口頭の支援です — 裁判所の期日、土地局、区役所、公証の署名、銀行、入管窓口など。書類の認証翻訳とは別のサービスで、多くの場面では両方が必要になります。",
    chain: [
      "場所、言語ペア、日時、想定所要時間を確認します。",
      "事前に通訳者へ案件背景と専門用語を共有します。",
      "書類一式を提供し、通訳の用語を書面ファイルと一致させます。",
      "通訳者がその予約に同行・出席します。",
      "事後の要点整理と、場所が求める書面翻訳を行います。",
    ],
    checklist: [
      "場所、部屋番号、予約時刻。",
      "言語ペア（必要に応じて方言も）。",
      "話題となる書類（事前にご提供ください）。",
      "出席者の氏名と立場（正しい敬称のため）。",
    ],
    pitfalls: [
      "希少な言語ペアの予約が直前になる。",
      "書類の事前共有がなく、窓口で用語がその場対応になる。",
      "通訳者がその場で認証書面翻訳も出せると誤解する。",
      "所要時間を短く見積もり、後続の予定に影響する。",
    ],
    remote: "場所が認める場合は遠隔通訳も可能です。裁判所と登記機関は通常出席を求めます。",
  },
  embassy: {
    label: "大使館・領事関係の書類サービス",
    definition:
      "国外で使う多くの書類にとって、大使館認証が最後の工程です。外務省認証の後に、在タイ当該国大使館が追加で認証します。予約方法・要求される文言・指定翻訳者の有無は大使館ごとに異なります。",
    chain: [
      "指定翻訳者リストの有無を含め、当該大使館の現行要件を確認します。",
      "それに沿ってタイ語原本と認証翻訳を準備します。",
      "大使館が求める場合は先に外務省認証を完了します。",
      "予約して提出し、認証済み書類を受領します。",
      "受理側が確認しやすいよう提出チェックリストを添えて納品します。",
    ],
    checklist: [
      "署名と押印のあるタイ語原本。",
      "必要言語への認証翻訳。",
      "名義人のパスポートまたはIDカード。",
      "大使館の書式と委任状。",
    ],
    pitfalls: [
      "大使館の指定リスト外の翻訳者を使う。",
      "外務省認証の前に大使館へ提出してしまう。",
      "大使館ごとに書式・文言が異なることを見落とす。",
      "予約枠が限られる時期を日程に織り込まない。",
    ],
    remote: "書類準備と提出は当方で対応できます。一部の大使館は名義人の出頭を求めます。",
  },
  ip: {
    label: "知的財産（商標・著作権）書類サービス",
    definition:
      "タイの知財業務は知的財産局（DIP）での商標出願と維持を中心に、著作権の届出、譲渡・ライセンス書類を含みます。国外で署名する場合は公証認証と領事認証が必要になるのが通例です。",
    chain: [
      "出願前の調査と区分の選定を行います。",
      "願書、標章見本、委任状を準備します。",
      "出願し審査を追跡、拒絶理由に応答します。",
      "国外署名の委任状・譲渡・ライセンスに認証と領事認証を行います。",
      "譲渡・ライセンスの記録、更新期限の管理を行います。",
    ],
    checklist: [
      "実際の使用態様と完全に一致する標章見本。",
      "出願人の会社書類または身分証明。",
      "区分決定のための商品・役務リスト。",
      "署名済み委任状（国外署名は公証・領事認証が必要）。",
      "先使用を主張する場合の使用証拠。",
    ],
    pitfalls: [
      "調査なしに出願し、後に異議を受ける。",
      "区分リストが狭すぎて実際の事業を覆えない。",
      "国外署名の委任状に必要な認証がない。",
      "更新や応答の期限を徒過する。",
    ],
    remote: "出願と審査対応は遠隔で可能です。署名認証のみ出席が必要な場合があります。",
  },
  litigation: {
    label: "訴訟サポート・裁判書類サービス",
    definition:
      "訴訟サポートは、訴状、証拠一式、外国語証拠の認証翻訳、公証を受けた宣誓供述書と委任状を扱います。方式の不備で却下されないよう、裁判所が受け入れる形式で準備します。",
    chain: [
      "案件評価：管轄裁判所、時効、証拠の不足点。",
      "裁判所の書式に沿って訴状と証拠冊子を準備します。",
      "外国語証拠ごとに認証翻訳を作成します。",
      "国外署名の書類に公証認証と領事認証を行います。",
      "提出、弁護士による出廷、判決後の各手続を行います。",
    ],
    checklist: [
      "契約書、往復書簡、書類の連鎖全体。",
      "当事者のパスポートまたは会社書類。",
      "弁護士への委任状（国外署名は認証が必要）。",
      "外国判決・仲裁判断とその認証翻訳。",
    ],
    pitfalls: [
      "認証タイ語翻訳を付けずに外国語証拠を提出する。",
      "国外署名の委任状に公証・領事認証がない。",
      "証拠を集めている間に時効が完成する。",
      "結果の保証を期待する — どの弁護士も保証はできません。",
    ],
    remote: "起案と証拠作業は遠隔で可能です。期日は弁護士が出廷します。",
  },
  pdpa: {
    label: "PDPA（個人情報保護法）対応書類サービス",
    definition:
      "タイの個人情報保護法（PDPA）は、個人データの取扱いに書面の根拠を求めます：プライバシー通知、同意記録、データ処理契約、処理活動記録、漏えい対応手順です。",
    chain: [
      "データ棚卸し：どの個人データを、どの目的で保有し、どこへ流れるか。",
      "処理目的ごとに適法根拠を選定し記録します。",
      "プライバシー通知、同意文言、データ処理契約を起案します。",
      "処理活動記録と漏えい対応手順を整備します。",
      "従業員研修を行い、事業の変化に応じて定期的に見直します。",
    ],
    checklist: [
      "個人データを保有するシステムとベンダーの一覧。",
      "既存のプライバシー通知と同意文言。",
      "データ処理を伴うベンダー契約。",
      "越境移転がある場合はその詳細。",
    ],
    pitfalls: [
      "海外の GDPR 通知をそのまま流用し、PDPA の要件に対応していない。",
      "同意が抱き合わせになっており、根拠として使えない。",
      "処理活動記録がない — 監査で最初に求められる書類です。",
      "ベンダー契約にデータ処理条項がない。",
    ],
    remote: "全工程を遠隔で対応でき、ワークショップはビデオ会議で実施します。",
  },
  "misc-official": {
    label: "その他の公的書類サービス",
    definition:
      "単一の省庁に収まらない公的書類を扱います：各種免許、許可、証明、記録の取得、そしてタイ国内または国外で使用するための認証と翻訳です。",
    chain: [
      "発行機関と現行の書式を特定します。",
      "証拠リストを整え、身分書類を照合します。",
      "提出し、受付番号で進捗を追跡します。",
      "書類を国外に持ち出す場合は認証翻訳を作成します。",
      "必要に応じて外務省認証と大使館認証を行います。",
    ],
    checklist: [
      "申請人のパスポートまたはタイIDカード。",
      "既存の証明・免許・受付番号。",
      "機関の書式に記載された補足証拠。",
      "当方が代理提出する場合の委任状。",
    ],
    pitfalls: [
      "誤った機関に提出し、並び直しになる。",
      "証拠リストが不完全である。",
      "タイ語原本が確定する前に翻訳してしまう。",
      "補足書類の有効期限を見落とす。",
    ],
    remote: "代理が認められる範囲で、提出と追跡は当方が対応します。",
  },
  council: {
    label: "タイ弁護士会（Lawyers Council）関連の認証サービス",
    definition:
      "一部の認証はタイ弁護士会（Lawyers Council of Thailand）登録弁護士でなければ行えません。公証業務弁護士の業務や、国外使用の書類・写しに対する弁護士認証がこれにあたります。",
    chain: [
      "受理機関が何を、どの文言で認証してほしいのかを確認します。",
      "署名者の本人確認と署名権限を検証します。",
      "弁護士会登録弁護士が認証し、登録簿に記録します。",
      "必要に応じて外務省認証と目的国大使館の認証を行います。",
      "認証済み原本をチェックリストとともに納品します。",
    ],
    checklist: [
      "署名者ごとのパスポートまたはタイIDカード。",
      "未署名で文言が確定した書類。",
      "会社として署名する場合の授権書類。",
      "取得済みの受理機関の書面要件。",
    ],
    pitfalls: [
      "認証の予約前に書類へ署名してしまう。",
      "原本を求められているのに写しだけを認証する。",
      "氏名表記がパスポートと一致していない。",
      "目的国が求める領事認証の工程を省く。",
    ],
    remote: "点検と後工程は遠隔で対応できます。署名の立会いは対面が必要です。",
  },
  "due-diligence": {
    label: "デューデリジェンス・真正性確認サービス",
    definition:
      "デューデリジェンスは相手方の説明を検証する作業です：法人の存続状態、取締役・株主、土地の権利、訴訟リスク、書類の真正性を、口頭の保証ではなく登記機関の記録に基づいて確認します。",
    chain: [
      "範囲を定めます：対象主体、資産、答えるべきリスク項目。",
      "参照可能な範囲で登記記録を取得します（DBD、土地局、裁判所索引）。",
      "書類の真正性と署名権限を検証します。",
      "重要な発見と根拠書類に認証翻訳を付します。",
      "リスク順に整理した書面報告と、次の手順の提案を提出します。",
    ],
    checklist: [
      "相手方の名称、登録番号、住所。",
      "土地権利証または資産の識別情報。",
      "契約ドラフトと相手方が行った表明保証。",
      "相手方から既に提供された書類。",
    ],
    pitfalls: [
      "相手方提供の書類のみに依拠し、登記機関で検証しない。",
      "土地局での抵当権・負担の調査を省く。",
      "報告書の完成前に署名してしまう。",
      "調査結果が長期間有効だと考える — 記録は変動します。",
    ],
    remote: "登記調査と報告は遠隔で完了できます。現地確認は別途手配します。",
  },
  pension: {
    label: "年金・海外給付に関する書類サービス",
    definition:
      "海外の年金・給付機関は、生存証明、住所証明、婚姻状況の確認、銀行情報の検証を定期的に求めます。多くの場合、認証と領事認証を経ないと受理されません。",
    chain: [
      "その機関が求める正確な書式と認証レベルを確認します。",
      "書式に記入し、身分と住所の証明を準備します。",
      "公証業務弁護士または弁護士が署名と写しを認証します。",
      "機関が求める場合は外務省認証と大使館認証を行います。",
      "機関へ提出または発送し、受領を確認します。",
    ],
    checklist: [
      "パスポートとタイの在留・入管関係書類。",
      "その機関の書式（未署名のもの）。",
      "タイの住所証明。",
      "給付や婚姻状況に関わる場合の婚姻・身分書類。",
      "指定の形式による銀行情報。",
    ],
    pitfalls: [
      "認証の予約前に生存証明へ署名してしまう。",
      "年次期限を逃し、支給が停止されるおそれ。",
      "認証レベルが機関の要求より低い。",
      "住所証明が入管記録と一致していない。",
    ],
    remote: "署名の立会い以外は当方が代行できます。",
  },
  "work-permit": {
    label: "ワークパーミット（労働許可証）書類サービス",
    definition:
      "タイのワークパーミットは、雇用主側と被雇用者側の二組の証拠に基づき労働省雇用局が発給します。当方は書類準備と順序設計、関連するビザ工程の助言を担当し、発給の判断は所管機関が行います。",
    chain: [
      "許可の種別、雇用主の適格性、現行の書類リストを確認します。",
      "雇用主側書類を準備します：会社証明書、財務諸表、従業員比率の証明。",
      "被雇用者側書類を準備します：学歴、推薦状、健康診断書、写真。",
      "海外の学歴には認証翻訳と（求められる場合）領事認証を行います。",
      "提出・追跡し、許可証とビザ／延長の期限を揃えます。",
    ],
    checklist: [
      "雇用主の会社証明書、DBD 抄本、VAT 登録。",
      "被雇用者のパスポートと正しいビザ種別。",
      "学位記・成績証明書とその認証翻訳。",
      "現行規格に合う健康診断書。",
      "雇用契約書と職務内容。",
    ],
    pitfalls: [
      "観光ビザで入国したが、その経路には別のビザ種別が必要だった。",
      "海外の学歴に認証翻訳・領事認証がない。",
      "提出時点で雇用主の従業員比率や登録資本が要件に達していない。",
      "ワークパーミットとビザの満了日が揃っていない。",
    ],
    remote: "準備と提出支援は遠隔で可能です。健康診断と窓口手続はご本人の出席が必要です。",
  },
  expert: {
    label: "複雑案件・特別対応の書類サービス",
    definition:
      "標準の流れに当てはまらない案件があります：複数国にまたがる認証チェーン、機関固有の文言、過去に差し戻された書類、特定の順序を要する多段階の認証など。こうした案件は顧問主導で進めます。",
    chain: [
      "個別審査：受理機関の書面要件が実際に何を求めているかを読み解きます。",
      "認証チェーンと、守るべき順序を設計します。",
      "追加の認証に進む前に、既存書類の不備を是正します。",
      "チェーン全体を実行します：翻訳、公証認証、外務省認証、大使館認証。",
      "受理側が段階ごとに確認できる提出メモを添えて納品します。",
    ],
    checklist: [
      "差し戻された版を含め、これまでに作成した全ての書類。",
      "受理機関からの書面による要件・拒否理由の通知。",
      "使用する国と提出先、そして期限。",
      "関係者のパスポートまたは会社書類。",
    ],
    pitfalls: [
      "差し戻し理由を読み解かないまま同じ順序で再提出する。",
      "複数国にまたがる工程を並行処理し、順序が崩れる。",
      "原本の再取得に要する時間を日程に入れていない。",
      "非公式な助言に基づき、機関の書面要件を確認しない。",
    ],
    remote:
      "審査・設計・調整は遠隔で対応できます。署名と一部の窓口工程のみご本人の関与が必要です。",
  },
};
